import { useEffect, useState } from 'react';
import PageHero from '@/components/PageHero';
import { ErrorBox, Loading } from '@/components/Status';
import {
  CertData,
  TraceData,
  getCattleTrace,
  getCert,
} from '@/api';
import '../styles/trace.css';

const EXAMPLE_TAG = 'TAG202608020001';

const EVENT_TYPES: Record<number, { label: string; color: string }> = {
  1: { label: '出生', color: '#2f8f5b' },
  2: { label: '转栏', color: '#3b6ea5' },
  3: { label: '免疫', color: '#b7791f' },
  4: { label: '出栏', color: '#c05621' },
  5: { label: '交易', color: '#d9a441' },
  6: { label: '屠宰', color: '#5d6d72' },
};

const STATUS_LABELS: Record<number, string> = {
  0: '在栏',
  1: '已售',
  2: '死亡',
  3: '屠宰',
};

const GENDER_LABELS: Record<number, string> = {
  0: '母',
  1: '公',
};

const SOURCE_LABELS: Record<number, string> = {
  1: '自繁',
  2: '外购',
  3: '进口',
};

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function eventMeta(type: number) {
  return EVENT_TYPES[type] ?? { label: `事件 ${type}`, color: '#5d6d72' };
}

function CattleCard({ data }: { data: TraceData }) {
  const cattle = data.cattle;
  const fields = [
    { label: '耳标号', value: cattle.ear_tag_no },
    { label: 'RFID 标签', value: cattle.rfid_tag || '—' },
    { label: '品种', value: cattle.breed || '—' },
    {
      label: '性别',
      value:
        cattle.gender !== undefined ? GENDER_LABELS[cattle.gender] ?? '—' : '—',
    },
    {
      label: '体重',
      value: cattle.weight !== undefined ? `${cattle.weight} kg` : '—',
    },
    { label: '毛色', value: cattle.coat_color || '—' },
    { label: '出生日期', value: formatDate(cattle.birth_date) },
    { label: '养殖场', value: cattle.farm_name || '—' },
    { label: '健康状态', value: cattle.health_status || '—' },
    { label: '检疫证号', value: cattle.quarantine_cert || '—' },
    {
      label: '来源',
      value:
        cattle.source_type !== undefined
          ? SOURCE_LABELS[cattle.source_type] ?? '—'
          : '—',
    },
    {
      label: '当前状态',
      value:
        cattle.status !== undefined
          ? STATUS_LABELS[cattle.status] ?? '—'
          : '—',
    },
  ];

  return (
    <article className="cattle-card">
      <header className="cattle-card-head">
        <div>
          <h3>牛只档案卡</h3>
          <p>耳标号：{cattle.ear_tag_no}</p>
        </div>
        <span className="health-badge">
          <span className="health-dot" />
          {cattle.health_status || '未知'}
        </span>
      </header>
      <dl className="cattle-fields">
        {fields.map((f) => (
          <div key={f.label} className="cattle-field">
            <dt>{f.label}</dt>
            <dd>{f.value}</dd>
          </div>
        ))}
      </dl>
    </article>
  );
}

function Timeline({ data }: { data: TraceData }) {
  const traces = [...data.traces].sort(
    (a, b) => new Date(a.event_time).getTime() - new Date(b.event_time).getTime(),
  );

  if (traces.length === 0) {
    return (
      <div className="empty-hint">
        <p>该牛只暂无溯源记录</p>
      </div>
    );
  }

  return (
    <div className="timeline">
      {traces.map((t) => {
        const meta = eventMeta(t.event_type);
        return (
          <div
            key={t.id}
            className="tl-item"
            style={{ '--dot-color': meta.color } as React.CSSProperties}
          >
            <div className="tl-meta">
              <span className="tl-label" style={{ color: meta.color }}>
                {meta.label}
              </span>
              <time>{formatDateTime(t.event_time)}</time>
            </div>
            <div className="tl-body">
              <p className="tl-desc">{t.event_desc || meta.label}</p>
              {t.location && <p className="tl-loc">📍 {t.location}</p>}
              {t.operator && <p className="tl-op">操作人：{t.operator}</p>}
              {t.remark && <p className="tl-remark">备注：{t.remark}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Trace() {
  const [tag, setTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TraceData | null>(null);

  const [txHash, setTxHash] = useState('');
  const [certLoading, setCertLoading] = useState(false);
  const [certError, setCertError] = useState<string | null>(null);
  const [cert, setCert] = useState<CertData | null>(null);

  useEffect(() => {
    document.title = '溯源查询 | 云牛集·山东牛产业智慧平台';
  }, []);

  const runQuery = async (value: string) => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const result = await getCattleTrace(value);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : '查询失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const value = tag.trim();
    if (!value) return;
    void runQuery(value);
  };

  const handleCertSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const value = txHash.trim();
    if (!value) return;
    setCertLoading(true);
    setCertError(null);
    setCert(null);
    try {
      const result = await getCert(value);
      setCert(result);
    } catch (err) {
      setCertError(err instanceof Error ? err.message : '验证失败，请稍后重试');
    } finally {
      setCertLoading(false);
    }
  };

  return (
    <>
      <PageHero
        title="溯源查询"
        desc="输入牛只耳标号，即可查看牛只档案卡与全生命周期溯源时间线。"
      />

      <section className="section">
        <div className="container">
          <div className="trace-search-card">
            <form onSubmit={handleSubmit} className="trace-form">
              <label htmlFor="ear-tag" className="sr-only">
                牛只耳标号
              </label>
              <input
                id="ear-tag"
                type="text"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                placeholder={`请输入耳标号，如 ${EXAMPLE_TAG}`}
                autoComplete="off"
                maxLength={64}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || tag.trim().length === 0}
              >
                {loading ? '查询中…' : '查询溯源'}
              </button>
            </form>
            <p className="trace-hint">
              公开接口免鉴权；可点击示例耳标{' '}
              <button
                type="button"
                className="link-like"
                onClick={() => {
                  setTag(EXAMPLE_TAG);
                  void runQuery(EXAMPLE_TAG);
                }}
              >
                {EXAMPLE_TAG}
              </button>{' '}
              体验查询。
            </p>
          </div>

          {loading && <Loading label="正在查询牛只档案…" />}

          {!loading && error && (
            <ErrorBox message={error} onRetry={() => void runQuery(tag.trim())} />
          )}

          {!loading && data && (
            <div className="trace-result">
              <CattleCard data={data} />
              <div className="timeline-card">
                <h3>溯源时间线</h3>
                <p className="timeline-note">
                  共 {data.traces.length} 条记录 · 按事件时间正序排列
                </p>
                <Timeline data={data} />
              </div>
            </div>
          )}

          <div className="cert-card">
            <div className="cert-head">
              <div>
                <h3>区块链存证验证</h3>
                <p>输入交易哈希（tx_hash），核验链上存证信息</p>
              </div>
              <span className="cert-badge">免鉴权</span>
            </div>
            <form onSubmit={handleCertSubmit} className="cert-form">
              <input
                type="text"
                value={txHash}
                onChange={(e) => setTxHash(e.target.value)}
                placeholder="如 0xabcd1234...（交易哈希）"
                autoComplete="off"
                maxLength={128}
              />
              <button
                type="submit"
                className="btn btn-teal"
                disabled={certLoading || txHash.trim().length === 0}
              >
                {certLoading ? '验证中…' : '验证存证'}
              </button>
            </form>

            {certLoading && <Loading label="正在验证链上存证…" />}
            {!certLoading && certError && <ErrorBox message={certError} />}
            {!certLoading && cert && !cert.found && (
              <div className="cert-result not-found">
                <p>未找到该交易哈希对应的链上存证记录，请确认哈希是否正确。</p>
              </div>
            )}
            {!certLoading && cert && cert.found && (
              <div className="cert-result">
                <div className="cert-row">
                  <span>验证结果</span>
                  <strong className="cert-ok">✓ 链上存证已确认</strong>
                </div>
                <div className="cert-row">
                  <span>交易哈希</span>
                  <code>{cert.tx_hash}</code>
                </div>
                <div className="cert-row">
                  <span>区块高度</span>
                  <strong>{cert.block_number}</strong>
                </div>
                <div className="cert-row">
                  <span>出块时间</span>
                  <strong>{cert.block_time ? formatDateTime(cert.block_time) : '—'}</strong>
                </div>
                <div className="cert-row">
                  <span>交易状态</span>
                  <strong>{cert.tx_status === 1 ? '已确认' : `状态码 ${cert.tx_status}`}</strong>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
